import { ProductContext } from '../../../types/ProductContext';

export class Role {
    constructor(roles, accessLevel, companyAccesses, user, group, companies) {
        this.roles = roles;
        this.accessLevel = accessLevel;
        this.companyAccesses = companyAccesses;
        this.context = ProductContext.LUBES;
        this.user = user;

        if (group) {
            this.group = group;
        }

        if (companies?.length) {
            this.addCompanies(companies);
        }
    }

    // Companies are scoped to Lubes by the API.
    isOneCompanyUser() {
        if (!this.companies) {
            return false;
        }

        return this.companies.length === 1;
    }

    getCompanyAccess(companyId) {
        if (!this.companyAccesses || !companyId) {
            return undefined;
        }

        return this.companyAccesses.find((companyAccess) => companyAccess.companyId === companyId);
    }

    getCompany(companyId) {
        if (!this.companies || !companyId) {
            return undefined;
        }

        return this.companies.find((company) => company.id === companyId);
    }

    // Companies are scoped to Lubes by the API.
    getCompanies() {
        return this.companies ?? [];
    }

    hasTermsAndConditionsAccepted() {
        if (this.isSuperAdmin()) {
            return true;
        }

        if (!this.group) {
            return false;
        }

        return this.group.gtcAccepted;
    }

    isSpotAllowed(companyId) {
        if (!this.companies || !companyId) {
            return false;
        }

        if (this.isSupplier()) return this.getCompany(companyId).supplierGroup.spotAllowed;

        return this.getCompany(companyId).spotAllowed;
    }

    hasAnyAdminRights() {
        if (this.isSuperAdmin()) {
            return true;
        }

        if (this.checkAdminRights(this.accessLevel)) {
            return true;
        }

        return (
            this.companyAccesses &&
            this.companyAccesses.find((companyAccess) => this.checkAdminRights(companyAccess.accessLevel))
        );
    }

    hasAnyCompanyWriteRights() {
        if (this.isSuperAdmin()) {
            return true;
        }

        return (
            this.companyAccesses &&
            this.companyAccesses.find((companyAccess) => this.checkWriteRights(companyAccess.accessLevel))
        );
    }

    checkWriteRights(level) {
        return level === 'ADMIN' || level === 'WRITE';
    }

    hasWriteRights(companyId) {
        if (this.isSuperAdmin()) {
            return true;
        }

        if (!companyId) {
            return this.checkWriteRights(this.accessLevel);
        }

        const companyAccess = this.getCompanyAccess(companyId);
        if (companyAccess) {
            return this.checkWriteRights(companyAccess.accessLevel);
        }

        return false;
    }

    checkAdminRights(level) {
        return level === 'ADMIN';
    }

    hasAdminRights(companyId) {
        if (this.isSuperAdmin()) {
            return true;
        }

        if (!companyId) {
            return this.checkAdminRights(this.accessLevel);
        }

        const companyAccess = this.getCompanyAccess(companyId);
        if (companyAccess) {
            return this.checkAdminRights(companyAccess.accessLevel);
        }

        return false;
    }

    hasRole(role) {
        return this.roles.indexOf(role) >= 0;
    }

    isSupplier() {
        return this.hasRole('ROLE_SUPPLIER');
    }

    isCustomer() {
        return this.hasRole('ROLE_CUSTOMER');
    }

    isSuperAdmin() {
        return this.hasRole('ROLE_ADMIN');
    }

    addCompanies(companies) {
        this.companies = companies;
        this.context = ProductContext.LUBES;
    }
}
